/**
 * GA-012 — equipment "Next Service" derivation.
 *
 * The backend leaves `next_service_due` empty when equipment is created with a
 * service interval but no purchase date, so maintenance alerts never trigger.
 * At display time we derive the due date by anchoring the interval to the
 * first available of: last_service_date, purchase_date, created_at.
 */

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function toValidDate(value) {
  if (value == null || value === '') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Returns { date: Date|null, derived: boolean }.
 * `derived` is true when the date was computed client-side (not persisted).
 */
export function getEffectiveNextServiceDue(equipment) {
  if (!equipment) return { date: null, derived: false };

  const stored = toValidDate(equipment.next_service_due);
  if (stored) return { date: stored, derived: false };

  const intervalDays = parseInt(equipment.service_interval_days, 10);
  if (!intervalDays || intervalDays <= 0) return { date: null, derived: false };

  const anchor =
    toValidDate(equipment.last_service_date) ||
    toValidDate(equipment.purchase_date) ||
    toValidDate(equipment.created_at);
  if (!anchor) return { date: null, derived: false };

  const due = new Date(anchor);
  due.setDate(due.getDate() + intervalDays);
  return { date: due, derived: true };
}

/** Whole days from today until the given date (negative when past). */
export function daysUntil(date) {
  if (!date) return null;
  return Math.ceil((date.getTime() - Date.now()) / MS_PER_DAY);
}

/** True when the (derived) next service date is past due or within `windowDays`. */
export function isServiceDueSoon(equipment, windowDays = 7) {
  if (equipment?.status !== 'ACTIVE') return false;
  const { date } = getEffectiveNextServiceDue(equipment);
  if (!date) return false;
  return daysUntil(date) <= windowDays;
}
